import type { OrderResponse } from '@palaccount/contracts';
import type { TFunction } from 'i18next';
import { Loader2, Pencil, Receipt, RotateCcw, Truck, XCircle } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { ConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { Button } from '@/components/ui/button';
import { useRateWarningGate } from '@/features/documents/fx-panel';
import { denyPermission } from '@/lib/permissions';
import { ApiError, localizeServerCause } from '@/lib/server-messages';

import { useCancelOrder, useConfirmOrder, useCreateOrderInvoice, useReopenOrder } from './orders-api';

/**
 * Wave 4B — THE SALES ORDER'S ACTS, IN ONE PLACE.
 *
 * Confirm (with BR-MD-11-14's rate question and BR-MD-01-10's credit
 * "Continue?"), Create invoice, Create delivery note, Amend (wave 4B's
 * reopen, a typed reason) and Cancel (GEN-59, a typed reason) used to live
 * only on the list-and-detail screen (`orders-page.tsx`). The order FORM now
 * carries them too, and the only honest way to put the same acts on two
 * screens is to write them once: this hook holds every decision — the
 * mutations, the refusal handling, which 409 is a question and which is an
 * error — and the two components below only DRAW.
 *
 * Why a hook and not a component: the two question boxes (rate, credit) are
 * AWAITED, so they must be rendered where the selection cannot unmount them
 * mid-question (the list page's lesson — see `useRateWarningGate`). The
 * page therefore mounts `OrderDialogs` once at its own level and
 * `OrderActionButtons` inside whatever condition draws the buttons.
 */
export interface OrderActs {
  busy: boolean;
  /** The last failure, localized; `null` after a success or a question. */
  error: string | null;
  clearError: () => void;
  /** Confirm: the rate question first, then the server, then the credit question if asked. */
  confirm: (order: OrderResponse) => void;
  /** One POST, then the new invoice opens (BR-SAL-02-08's link made on the way). */
  createInvoice: () => void;
  openCancel: () => void;
  closeCancel: () => void;
  cancelAsk: boolean;
  cancel: (reason: string) => void;
  openReopen: () => void;
  closeReopen: () => void;
  reopenAsk: boolean;
  reopen: (reason: string) => void;
  /** BR-MD-01-10's Warn-only question, held whole while on screen. */
  creditAsk: ApiError | null;
  answerCredit: (proceed: boolean) => void;
  /** BR-MD-11-14's question, in ADM-02 §8's shared box. */
  rateWarningDialog: ReactNode;
}

/** Which screen draws the acts — two screens, two fallback sentences. */
export type OrderActsScreen = 'list' | 'editor';

/**
 * A refusal keeps the server's sentence localized; a request that never
 * answered gets the SCREEN's own sentence. Each key is written as a literal
 * so `server-cause-keys-exist.test.ts` can check both dictionaries hold it.
 */
function localizeOrderCause(t: TFunction, thrown: unknown, screen: OrderActsScreen): string {
  return screen === 'list'
    ? localizeServerCause(t, thrown, 'orders.errSaveFailedList')
    : localizeServerCause(t, thrown, 'orders.errSaveFailedEditor');
}

export function useOrderActions(
  /** The order the acts are for — the URL's id, so they are never one click behind. */
  orderId: string,
  screen: OrderActsScreen,
): OrderActs {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirmOrder = useConfirmOrder(orderId);
  const cancelOrder = useCancelOrder(orderId);
  const reopenOrder = useReopenOrder(orderId);
  const createInvoice = useCreateOrderInvoice(orderId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelAsk, setCancelAsk] = useState(false);
  const [reopenAsk, setReopenAsk] = useState(false);
  const [creditAsk, setCreditAsk] = useState<ApiError | null>(null);
  const { confirmRateWarning, rateWarningDialog } = useRateWarningGate();

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.approve');
      // `localizeServerCause` keeps the cause: a refusal keeps the server's
      // sentence localized, a request that never answered gets the screen's
      // own rather than the browser's `Failed to fetch`.
      setError(localizeOrderCause(t, thrown, screen));
    } finally {
      setBusy(false);
    }
  };

  /**
   * The confirm click's own path rather than `act`: a 409 whose body carries
   * `overCreditLimit: true` is BR-MD-01-10's Warn-only QUESTION, not an
   * error — it opens the GEN-205 box instead of painting the red strip.
   */
  const confirmNow = async (continueOverLimit: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await confirmOrder.mutateAsync(continueOverLimit ? { continueOverLimit: true } : undefined);
    } catch (thrown) {
      if (
        thrown instanceof ApiError &&
        thrown.status === 409 &&
        typeof thrown.body === 'object' &&
        thrown.body !== null &&
        (thrown.body as { overCreditLimit?: boolean }).overCreditLimit === true
      ) {
        setCreditAsk(thrown);
        return;
      }
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.approve');
      setError(localizeOrderCause(t, thrown, screen));
    } finally {
      setBusy(false);
    }
  };

  return {
    busy,
    error,
    clearError: () => setError(null),
    confirm: (target) => {
      // BR-MD-11-14 at the confirming act: the click that freezes the rate is
      // the click that asks the question, BEFORE the act, so `busy` stays
      // false while it is on screen.
      void (async () => {
        if (!(await confirmRateWarning(target.rateWarning))) return;
        await confirmNow(false);
      })();
    },
    createInvoice: () =>
      void act(async () => {
        const result = await createInvoice.mutateAsync();
        void navigate(`/sales/${result.invoiceId}`);
      }),
    openCancel: () => setCancelAsk(true),
    closeCancel: () => setCancelAsk(false),
    cancelAsk,
    cancel: (reason) => {
      setCancelAsk(false);
      void act(() => cancelOrder.mutateAsync(reason));
    },
    openReopen: () => setReopenAsk(true),
    closeReopen: () => setReopenAsk(false),
    reopenAsk,
    reopen: (reason) => {
      setReopenAsk(false);
      void act(() => reopenOrder.mutateAsync(reason));
    },
    creditAsk,
    answerCredit: (proceed) => {
      setCreditAsk(null);
      if (proceed) void confirmNow(true);
    },
    rateWarningDialog,
  };
}

/** Whether the order's status admits each act — the STATUS's answer, before the role's. */
export function orderActVisibility(order: OrderResponse) {
  const open = order.status === 'confirmed' || order.status === 'partially_delivered';
  return {
    edit: order.status === 'draft',
    confirm: order.status === 'draft',
    /* BR-SAL-02-06: hidden on a cancelled order — and on a draft, which has
       nothing to bill yet. A delivered or fulfilled order still bills. */
    createInvoice: (open || order.status === 'fulfilled') && !order.invoiceId,
    /* BR-SAL-03: deliverable while confirmed / partially delivered and not fully delivered. */
    createDelivery: open && Number(order.deliveredPct) < 100,
    /* Wave 4B: the way back to draft exists while the order is confirmed;
       the server names the document that blocks it, if any. */
    amend: open,
    /* BR-SAL-02-07: the Cancel button exists. */
    cancel: order.status !== 'cancelled' && order.status !== 'closed',
  };
}

/**
 * The buttons. The STATUS decides whether a control exists at all — a
 * confirmed order has no Edit for anybody, Owner included (BR-PERM-02) —
 * and the PERMISSION decides whether it is usable (16.7 keeps the two
 * apart, and so does this). `showEdit` is the list page's: the form IS the
 * editor and draws no link to itself.
 */
export function OrderActionButtons({
  order,
  acts,
  showEdit = false,
}: {
  order: OrderResponse;
  acts: OrderActs;
  showEdit?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const show = orderActVisibility(order);
  return (
    <>
      {showEdit && show.edit ? (
        <Gated permission="sales.edit" reasonKey="perm.cannot.editSalesOrders">
          <Button asChild variant="outline">
            <Link to={`/sales/orders/${order.id}/edit`}>
              <Pencil aria-hidden /> {t('common.edit')}
            </Link>
          </Button>
        </Gated>
      ) : null}
      {show.confirm ? (
        <Gated permission="sales.approve" reasonKey="perm.cannot.confirmSalesOrders">
          <Button onClick={() => acts.confirm(order)} disabled={acts.busy}>
            {acts.busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
            {t('orders.confirm')}
          </Button>
        </Gated>
      ) : null}
      {show.createDelivery ? (
        <Gated permission="sales.create" reasonKey="perm.cannot.createDeliveries">
          <Button asChild variant="outline">
            <Link to={`/sales/deliveries/new?orderId=${order.id}`}>
              <Truck aria-hidden /> {t('orders.createDelivery')}
            </Link>
          </Button>
        </Gated>
      ) : null}
      {show.createInvoice ? (
        <Gated permission="sales.create" reasonKey="perm.cannot.createInvoices">
          <Button variant="outline" onClick={acts.createInvoice} disabled={acts.busy}>
            <Receipt aria-hidden /> {t('orders.createInvoice')}
          </Button>
        </Gated>
      ) : null}
      {show.amend ? (
        <Gated permission="sales.approve" reasonKey="perm.cannot.confirmSalesOrders">
          {/* GEN-208: the click OPENS the question and does nothing else. */}
          <Button variant="outline" onClick={acts.openReopen} disabled={acts.busy}>
            <RotateCcw aria-hidden /> {t('orders.amend')}
          </Button>
        </Gated>
      ) : null}
      {show.cancel ? (
        <Gated permission="sales.approve" reasonKey="perm.cannot.cancelSalesOrders">
          <Button variant="destructive" onClick={acts.openCancel} disabled={acts.busy}>
            <XCircle aria-hidden /> {t('orders.cancelOrder')}
          </Button>
        </Gated>
      ) : null}
    </>
  );
}

/**
 * The questions, rendered ONCE at the page's level: the rate and credit
 * boxes are awaited and must not unmount with a changing selection; the two
 * typed-reason boxes (GEN-59's cancel, wave 4B's amend) stay inside the
 * status condition that allows the act, so a dialog cannot outlive it. The
 * list page keeps its own cancel `ReasonBox` beside this (`cancel={false}`)
 * — `gen209-confirm-sites.test.tsx` reads that screen's source for it.
 */
export function OrderDialogs({
  order,
  acts,
  screen,
  cancel = true,
}: {
  order: OrderResponse | undefined;
  acts: OrderActs;
  screen: OrderActsScreen;
  cancel?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const show = order === undefined ? null : orderActVisibility(order);
  return (
    <>
      {acts.rateWarningDialog}
      {/* BR-MD-01-10 Warn only — the BODY is the server's sentence, localized
          by cause; the verb is its own, never OK (GEN-206); Escape, backdrop
          and Cancel leave the draft exactly as it was (GEN-207). */}
      <ConfirmBox
        open={acts.creditAsk !== null}
        title={t('orders.creditLimitTitle')}
        body={acts.creditAsk ? localizeOrderCause(t, acts.creditAsk, screen) : ''}
        confirmLabel={t('orders.creditLimitContinue')}
        busy={acts.busy}
        onConfirm={() => acts.answerCredit(true)}
        onCancel={() => acts.answerCredit(false)}
      />
      {show?.amend ? (
        <ReasonBox
          open={acts.reopenAsk}
          title={t('orders.amendTitle')}
          prompt={t('orders.amendBody')}
          reasonLabel={t('orders.amendReasonLabel')}
          confirmLabel={t('orders.amendConfirm')}
          busy={acts.busy}
          onConfirm={acts.reopen}
          onCancel={acts.closeReopen}
        />
      ) : null}
      {cancel && show?.cancel ? (
        <ReasonBox
          open={acts.cancelAsk}
          title={t('orders.confirmCancelTitle')}
          prompt={t('orders.confirmCancelBody')}
          reasonLabel={t('gen72.cancelReasonLabel')}
          confirmLabel={t('gen72.confirmCancel')}
          busy={acts.busy}
          onConfirm={acts.cancel}
          onCancel={acts.closeCancel}
        />
      ) : null}
    </>
  );
}
